import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog, DialogContent 
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, Building2, Users, FileText, Heart, Package, 
  Calendar, ArrowRight, Command, X, Clock
} from 'lucide-react';

const SEARCH_CATEGORIES = [
  { 
    id: 'instituicoes', 
    label: 'Instituições', 
    icon: Building2, 
    color: 'text-blue-600 bg-blue-50',
    path: '/instituicoes'
  },
  { 
    id: 'utentes', 
    label: 'Utentes', 
    icon: Users, 
    color: 'text-green-600 bg-green-50',
    path: '/utentes'
  },
  { 
    id: 'voluntarios', 
    label: 'Voluntários', 
    icon: Heart, 
    color: 'text-pink-600 bg-pink-50',
    path: '/voluntariado'
  },
  { 
    id: 'recursos', 
    label: 'Recursos', 
    icon: Package, 
    color: 'text-amber-600 bg-amber-50',
    path: '/recursos'
  },
  { 
    id: 'documentos', 
    label: 'Documentos', 
    icon: FileText, 
    color: 'text-purple-600 bg-purple-50',
    path: '/documentos'
  }
];

const QUICK_ACTIONS = [
  { label: 'Nova Instituição', path: '/instituicoes?action=new', icon: Building2 },
  { label: 'Novo Voluntário', path: '/voluntariado?action=new', icon: Heart },
  { label: 'Dashboard Executivo', path: '/dashboard-executivo', icon: Calendar },
];

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('entrajudata_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Keyboard shortcut to open (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Search API
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/search?q=${encodeURIComponent(searchQuery)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
      } else {
        // Fallback: show category links
        setResults(SEARCH_CATEGORIES.filter(cat => 
          cat.label.toLowerCase().includes(searchQuery.toLowerCase())
        ).map(cat => ({
          type: 'category',
          ...cat
        })));
      }
    } catch (error) {
      // Fallback results
      setResults(SEARCH_CATEGORIES.filter(cat => 
        cat.label.toLowerCase().includes(searchQuery.toLowerCase())
      ).map(cat => ({
        type: 'category',
        ...cat
      })));
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const totalItems = results.length + QUICK_ACTIONS.length;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(selectedIndex);
    }
  };

  const handleSelect = (index) => {
    let path = '';
    
    if (index < results.length) {
      const result = results[index];
      path = result.path || `/${result.type}/${result.id}`;
      
      // Save to recent searches
      const newRecent = [
        { query, path, label: result.label || result.nome, type: result.type },
        ...recentSearches.filter(r => r.path !== path)
      ].slice(0, 5);
      setRecentSearches(newRecent);
      localStorage.setItem('entrajudata_recent_searches', JSON.stringify(newRecent));
    } else {
      const actionIndex = index - results.length;
      if (QUICK_ACTIONS[actionIndex]) {
        path = QUICK_ACTIONS[actionIndex].path;
      }
    }
    
    if (path) {
      navigate(path);
      setOpen(false);
      setQuery('');
    }
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('entrajudata_recent_searches');
  };

  return (
    <>
      {/* Search Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors w-full max-w-xs"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="flex-1 text-left">Pesquisar...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
          Ctrl+K
        </kbd>
      </button>

      {/* Search Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <Search className="w-5 h-5 text-slate-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pesquisar instituições, utentes, voluntários..."
              className="flex-1 border-0 shadow-none focus-visible:ring-0 text-lg bg-transparent"
              data-testid="global-search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="text-xs text-slate-400 border border-slate-200 dark:border-slate-600 px-1.5 py-0.5 rounded">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto">
            {/* Loading */}
            {loading && (
              <div className="px-4 py-8 text-center text-slate-500">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin mx-auto"></div>
                <p className="mt-2 text-sm">A pesquisar...</p>
              </div>
            )}

            {/* No Query - Show Recent & Quick Actions */}
            {!query && !loading && (
              <>
                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Pesquisas Recentes</p>
                      <button onClick={clearRecentSearches} className="text-xs text-slate-400 hover:text-slate-600">
                        Limpar
                      </button>
                    </div>
                    {recentSearches.map((recent, idx) => (
                      <button
                        key={idx}
                        onClick={() => navigate(recent.path)}
                        className="flex items-center gap-3 w-full px-3 py-2 text-left rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="flex-1 text-sm">{recent.label || recent.query}</span>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Quick Actions */}
                <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">Ações Rápidas</p>
                  {QUICK_ACTIONS.map((action, idx) => {
                    const Icon = action.icon;
                    const isSelected = selectedIndex === results.length + idx;
                    return (
                      <button
                        key={action.path}
                        onClick={() => { navigate(action.path); setOpen(false); }}
                        className={`flex items-center gap-3 w-full px-3 py-2 text-left rounded-lg transition-colors ${
                          isSelected ? 'bg-[#0066CC]/10 text-[#0066CC]' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="flex-1 text-sm">{action.label}</span>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    );
                  })}
                </div>

                {/* Categories */}
                <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">Explorar</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SEARCH_CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => { navigate(cat.path); setOpen(false); }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          <div className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-medium">{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Search Results */}
            {query && !loading && results.length > 0 && (
              <div className="px-2 py-2">
                {results.map((result, idx) => {
                  const category = SEARCH_CATEGORIES.find(c => c.id === result.type) || SEARCH_CATEGORIES[0];
                  const Icon = category.icon;
                  const isSelected = selectedIndex === idx;
                  
                  return (
                    <button
                      key={result.id || idx}
                      onClick={() => handleSelect(idx)}
                      className={`flex items-center gap-3 w-full px-3 py-3 text-left rounded-lg transition-colors ${
                        isSelected ? 'bg-[#0066CC]/10' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                          {result.nome || result.label}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {result.codigo_ea || result.email || category.label}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {category.label}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}

            {/* No Results */}
            {query && !loading && results.length === 0 && (
              <div className="px-4 py-8 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 dark:text-slate-400">Sem resultados para "{query}"</p>
                <p className="text-sm text-slate-400 mt-1">Tente outra pesquisa</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border">↑↓</kbd>
                Navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border">↵</kbd>
                Selecionar
              </span>
            </div>
            <span>Pesquisa global</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
